import { useEffect } from 'react';
import { useSharedValue, useAnimatedStyle, withTiming } from 'react-native-reanimated';
import { machineStatuses, ncFileStatuses } from '@components/wallboard/components/functions';
import { WALLBOARD_STATUS_CHANGE } from '@components/wallboard/constants';
import { getTimelineLineHeight } from '@components/wallboard/helpers/productivityTimeline';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { LevelSubScreen, Perspective } from '@v2/pages/common/enums';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { isWeb } from '@helpers';
import { trackEvent, TRACKING_EVENTS } from '@services/tracking';
import type { Machine } from '@components/wallboard/types';

export interface UseManageActiveTileParams {
  machine: Machine;
  machineStatus: NonNullable<Machine['machineStatus']>;
  scale: (v: number) => number;
  tileHeight: number;
  productionBatchId: number | null;
  oldMachineStatusDate: number | null;
  oldMachineStatus: Machine['machineStatus'] | null;
  tourIds?: Record<string, string>;
}

const useManageActiveTile = ({
  machine,
  machineStatus = {} as NonNullable<Machine['machineStatus']>,
  scale,
  tileHeight,
  productionBatchId,
  oldMachineStatusDate,
  oldMachineStatus,
  tourIds,
}: UseManageActiveTileParams) => {
  const { ncFileId, documentId, partDetails, ncFileStatus, ncFile, status, imageHash, imageDocumentId } = machineStatus;

  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();

  const { partId } = partDetails || {};

  const imageData: Record<string, any> = { documentId };

  if (partId) {
    imageData.p = partId;
  }
  if (ncFileId) {
    imageData.c = ncFileId;
  }

  const lineHeight = getTimelineLineHeight(tileHeight);

  const isNcFileChanged = ncFileStatus === ncFileStatuses.Change && status === machineStatuses.CHANGE;

  const needToShowStatusChange =
    oldMachineStatusDate &&
    new Date().getTime() - oldMachineStatusDate < WALLBOARD_STATUS_CHANGE.DISPLAY_DURATION &&
    oldMachineStatus?.status !== machineStatus.status;

  const showStatusChange = needToShowStatusChange && machineStatus.status !== machineStatuses.NOTPRODUCTIVE;

  const fadeAnim = useSharedValue(0);

  useEffect(() => {
    fadeAnim.value = withTiming(showStatusChange ? 1 : 0, { duration: WALLBOARD_STATUS_CHANGE.FADE_DURATION });
  }, [showStatusChange]);

  const productivityStyle = useAnimatedStyle(() => ({
    opacity: 1 - fadeAnim.value,
  }));

  const statusOverlayStyle = useAnimatedStyle(() => ({
    opacity: fadeAnim.value,
  }));

  const partImageWebProps = isWeb && tourIds?.partImage ? { dataSet: { tourId: tourIds.partImage } } : {};

  const onTilePress = ({ clickOnImage }: { clickOnImage: boolean }) => {
    trackEvent(TRACKING_EVENTS.WALLBOARD, {
      action: 'machineTapped',
      machineId: String(machine.machineId || ''),
      machineName: machine.shortName || '',
      machineProductivityStatus: machineStatus?.status || 'Not Productive',
      machineSubStatus: machineStatus?.subStatus,
      machineExecutionMode: machineStatus?.executionMode,
    });
    navigateToMainDetailsScreen({
      mainScreen: BatchDetailsScreenName,
      subScreen: BatchDetailsScreen.BATCH,
      levelSubScreen: clickOnImage ? LevelSubScreen.DOCUMENTS : LevelSubScreen.GENERAL,
      params: {
        perspective: Perspective.BATCH,
        batchNumber: machineStatus?.productionBatchDetails?.batchNumber,
        batchId: machineStatus?.productionBatchId,
        ncFileStatus,
        ...(clickOnImage
          ? {
              selectedImageKey: imageHash,
              documentId: imageDocumentId,
            }
          : {}),
      },
    });
  };

  const onNcFileSnapshotPress = () => {
    navigateToMainDetailsScreen({
      mainScreen: NCFileDetailsScreenName,
      subScreen: NCFileDetailsScreen.NC_FILE,
      levelSubScreen: LevelSubScreen.GENERAL,
      params: {
        perspective: Perspective.NC_FILE,
        ncFileId,
        ncFileName: ncFile,
        ncFileStatus,
      },
    });
  };

  return {
    onTilePress,
    onNcFileSnapshotPress,
    lineHeight,
    isNcFileChanged,
    showStatusChange,
    imageData,
    partId,
    fadeAnim,
    productivityStyle,
    statusOverlayStyle,
    partImageWebProps,
    ncFileId,
    documentId,
    partDetails,
    ncFileStatus,
    ncFile,
    status,
    imageHash,
    imageDocumentId,
  };
};

export default useManageActiveTile;
